import useSWR from 'swr';
import { billingService, isApiError } from '@dopomogai/api-client';
import type { PaginatedResponse, CreditTransactionItem } from '@dopomogai/types';
import { AgentKitError } from '../types';

/**
 * A React hook to fetch a paginated list of the authenticated user's credit transactions.
 * It abstracts the data fetching, loading, and error states for the transaction history UI.
 *
 * @param limit - The number of transactions to fetch per page.
 * @param offset - The starting index of transactions to fetch for pagination.
 * @returns An object containing the list of transactions, total count, loading state, and error state.
 *
 * @example
 * const { transactions, total, isLoading, error } = useCreditTransactions(25, 0);
 */
export const useCreditTransactions = (limit = 20, offset = 0) => {
  // The SWR key is a unique string that includes all parameters affecting the fetch.
  // This ensures that changing the page (and thus offset) triggers a new fetch.
  const swrKey = `/users/me/credit-transactions?limit=${limit}&offset=${offset}`;

  const { data, error, isLoading, mutate } = useSWR<
    PaginatedResponse<CreditTransactionItem>,
    AgentKitError
  >(
    swrKey,
    async () => {
      const res = await billingService.getCreditTransactions(limit, offset);
      if (isApiError(res)) {
        // When the fetcher throws an error, SWR automatically captures it
        // and places it in the `error` state.
        throw new AgentKitError('Failed to fetch credit transactions', res);
      }
      return res;
    },
    {
      // Optional: Good practice for paginated data to prevent aggressive re-fetching.
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    /** The array of credit transaction items for the current page. Defaults to an empty array. */
    transactions: data?.items ?? [],
    /** The total number of transactions available for the user. Defaults to 0. */
    total: data?.total ?? 0,
    /** A boolean indicating if the data for the current page is being fetched. */
    isLoading,
    /** An error object if the fetch failed, otherwise null. */
    error,
    /** The SWR mutate function to manually trigger a re-fetch of the data. */
    mutate,
  };
};